import { useEffect } from 'react';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/base/JsonLd';

export default function SkilledNursing() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://celebrity-homehealth.com';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'MedicalWebPage',
    '@id': `${siteUrl}/services/skilled-nursing#webpage`,
    url: `${siteUrl}/services/skilled-nursing`,
    name: 'Skilled Nursing Home Care Las Vegas | Celebrity Home Health',
    description: 'Expert skilled nursing care at home in Las Vegas, NV — wound care, IV therapy, medication management, vital signs monitoring, and more. Medicare certified, ACHC accredited.',
    isPartOf: { '@id': `${siteUrl}/#website` },
    medicalSpecialty: 'Nursing',
    lastReviewed: '2026-04-11',
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: 'Services', item: `${siteUrl}/services` },
        { '@type': 'ListItem', position: 3, name: 'Skilled Nursing', item: `${siteUrl}/services/skilled-nursing` },
      ],
    },
  };

  useEffect(() => {
    document.title = 'Skilled Nursing Home Care Las Vegas | Celebrity Home Health';
  }, []);

  const items = [
    { icon: 'ri-heart-pulse-line', title: 'Wound Care', desc: 'Expert assessment and dressing of surgical wounds, pressure ulcers, diabetic wounds, and complex injuries to promote optimal healing.' },
    { icon: 'ri-syringe-line', title: 'IV Therapy', desc: 'Administration of IV medications, hydration, and nutrition therapy in the comfort and safety of your home.' },
    { icon: 'ri-capsule-line', title: 'Medication Management', desc: 'Medication review, reconciliation, administration, and education to ensure safe and effective treatment regimens.' },
    { icon: 'ri-stethoscope-line', title: 'Vital Signs Monitoring', desc: 'Regular assessment of blood pressure, heart rate, temperature, oxygen saturation, and respiratory status.' },
    { icon: 'ri-drop-line', title: 'Catheter Care', desc: 'Professional urinary catheter insertion, maintenance, irrigation, and patient education for safe home management.' },
    { icon: 'ri-book-open-line', title: 'Disease Education', desc: 'Comprehensive patient and caregiver education on managing chronic conditions, recognizing warning signs, and preventing complications.' },
    { icon: 'ri-user-heart-line', title: 'Pain Management', desc: 'Evidence-based pain assessment and management strategies tailored to your individual needs and goals.' },
    { icon: 'ri-file-list-3-line', title: 'Care Coordination', desc: 'Coordination with your physician, specialists, and care team to ensure seamless, consistent, and high-quality care.' },
  ];

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
      <JsonLd data={jsonLd as Record<string, unknown>} id="json-ld-skilled-nursing" />
      <Header />

      <section className="relative pt-32 pb-20 bg-slate-900 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/f0c8d6951be7fa658976d796a8b526e4.jpg"
            alt="Skilled Nursing Home Care Las Vegas"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-900/85 to-slate-900/98"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-500/20 border border-amber-400/30 rounded-full text-amber-300 text-xs font-semibold uppercase tracking-wider mb-6">
              <i className="ri-nurse-line"></i> Skilled Nursing
            </div>
            <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Expert Skilled Nursing<br />Care at <span className="text-amber-400">Home</span>
            </h1>
            <p className="text-slate-300 text-xl leading-relaxed mb-8">
              Celebrity Home Health's licensed RNs and LPNs deliver hospital-level skilled nursing care right in your home — Medicare certified and ACHC accredited. Serving Las Vegas, Henderson, Summerlin, and surrounding communities.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href="/contact" className="inline-flex items-center justify-center gap-2 px-7 py-4 bg-amber-600 hover:bg-amber-500 text-white rounded-xl font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer">
                <i className="ri-calendar-check-line"></i> Request Nursing Care
              </a>
              <a href="tel:866-939-0330" className="inline-flex items-center justify-center gap-2 px-7 py-4 bg-white/10 hover:bg-white/15 border border-white/20 text-white rounded-xl font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer">
                <i className="ri-phone-line"></i> 866-939-0330
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-amber-600">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-3 gap-8 text-center text-white">
            <div><div className="text-4xl font-bold mb-1">24/7</div><div className="text-amber-100 text-sm">On-Call Support</div></div>
            <div className="border-x border-amber-500"><div className="text-4xl font-bold mb-1">Medicare</div><div className="text-amber-100 text-sm">Certified Care</div></div>
            <div><div className="text-4xl font-bold mb-1">ACHC</div><div className="text-amber-100 text-sm">Accredited Agency</div></div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <div className="inline-block px-3 py-1.5 bg-amber-100 text-amber-700 rounded-full text-xs font-semibold uppercase tracking-wider mb-4">What We Provide</div>
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Skilled Nursing Services</h2>
            <p className="text-slate-500 text-lg max-w-2xl mx-auto">Comprehensive clinical nursing care delivered by our experienced, compassionate licensed nurses.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {items.map((item, i) => (
              <div key={i} className="bg-stone-50 p-6 rounded-2xl border border-stone-200 hover:border-amber-300 transition-all duration-300">
                <div className="w-11 h-11 bg-amber-100 rounded-xl flex items-center justify-center mb-4">
                  <i className={`${item.icon} text-amber-700 text-lg`}></i>
                </div>
                <h3 className="font-bold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative w-full h-[440px] rounded-3xl overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=compassionate-experienced-female-registered-nurse-RN-sitting-beside-elderly-male-patient-in-comfortable-home-bedroom-reviewing-medication-chart-warm-natural-window-light-professional-home-health-skilled-nursing-care-Las-Vegas&width=700&height=440&seq=skilled-nursing-story-2026&orientation=landscape"
                alt="Skilled Nurse at home"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div>
              <div className="inline-block px-3 py-1.5 bg-amber-100 text-amber-700 rounded-full text-xs font-semibold uppercase tracking-wider mb-5">Who Benefits</div>
              <h2 className="text-4xl font-bold text-slate-900 mb-6 leading-tight">Is Skilled Nursing Right for You?</h2>
              <p className="text-slate-500 mb-6 leading-relaxed">Skilled nursing services are ordered by your physician and covered by Medicare when medically necessary. You may qualify if you are:</p>
              <ul className="space-y-3 mb-8">
                {[
                  'Recovering from surgery, illness, or hospitalization',
                  'Managing a complex wound, ostomy, or drains',
                  'Requiring IV therapy or injectable medications',
                  'Living with a chronic illness such as diabetes, CHF, or COPD',
                  'Needing medication education, management, or reconciliation',
                  'Homebound and requiring professional clinical oversight',
                ].map((point, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-5 h-5 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-amber-700 text-xs"></i>
                    </div>
                    <span className="text-slate-600 text-sm">{point}</span>
                  </li>
                ))}
              </ul>
              <a href="/contact" className="inline-flex items-center gap-2 px-6 py-3.5 bg-amber-600 hover:bg-amber-700 text-white rounded-xl font-semibold text-sm transition-all duration-300 whitespace-nowrap cursor-pointer">
                Request Skilled Nursing <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-900">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-4xl font-bold text-white mb-5">Ready to Get Started?</h2>
          <p className="text-slate-400 text-lg mb-8">Contact Celebrity Home Health today. Our care coordinators will verify your benefits and connect you with a skilled nurse within 24–48 hours.</p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href="/contact" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-amber-600 hover:bg-amber-500 text-white rounded-xl font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer">
              <i className="ri-calendar-check-line"></i> Request Care Now
            </a>
            <a href="/services" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 hover:bg-white/15 border border-white/20 text-white rounded-xl font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer">
              View All Services
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
